import type { LegacyEvent, StoredDayRoute, StoredWeek, WeeksFile } from "./types"
import { previewImageUrl, rwgpsIdFromUrl } from "./types"

export const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const

export type DayName = (typeof DAY_ORDER)[number]

export const DAY_LABELS: Record<DayName, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
}

export const DEFAULT_DAYS: DayName[] = ["tuesday", "thursday"]

export interface DayRoute {
  title: string
  url: string
  previewImageUrl?: string
  thumbLabel?: string
}

export interface WeekSchedule {
  key: string
  label: string
  activeDays: DayName[]
  days: Partial<Record<DayName, DayRoute>>
}

function sortDays(days: DayName[]): DayName[] {
  return days.slice().sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
}

/** ISO week key e.g. 2026-W22 (Monday-based, UTC). */
export function getCurrentWeekKey(date = new Date()): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`
}

/** Monday 00:00 UTC for the ISO week. */
export function mondayOfWeekKey(weekKey: string): Date {
  const match = weekKey.match(/^(\d{4})-W(\d{2})$/)
  if (!match) throw new Error(`Invalid week key: ${weekKey}`)
  const year = Number(match[1])
  const week = Number(match[2])
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4Day = jan4.getUTCDay() || 7
  const mondayWeek1 = new Date(jan4)
  mondayWeek1.setUTCDate(jan4.getUTCDate() - jan4Day + 1)
  const monday = new Date(mondayWeek1)
  monday.setUTCDate(mondayWeek1.getUTCDate() + (week - 1) * 7)
  return monday
}

export function dateForDayInWeek(weekKey: string, day: DayName): string {
  const monday = mondayOfWeekKey(weekKey)
  const offset = DAY_ORDER.indexOf(day)
  const d = new Date(monday)
  d.setUTCDate(monday.getUTCDate() + offset)
  return d.toISOString().slice(0, 10)
}

function dayFromDate(dateStr: string): DayName | null {
  const d = new Date(dateStr + "T12:00:00Z")
  const idx = (d.getUTCDay() + 6) % 7
  return DAY_ORDER[idx] ?? null
}

function formatShortDate(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
}

export function getWeekLabel(weekKey: string): string {
  const monday = mondayOfWeekKey(weekKey)
  const sunday = new Date(monday)
  sunday.setUTCDate(monday.getUTCDate() + 6)
  const year = monday.getUTCFullYear()
  return `${formatShortDate(monday)}–${formatShortDate(sunday)}, ${year}`
}

export function getWeekKeysWindow(centerKey: string, radius = 8): string[] {
  const center = mondayOfWeekKey(centerKey)
  const keys: string[] = []
  for (let i = -radius; i <= radius; i++) {
    const d = new Date(center)
    d.setUTCDate(center.getUTCDate() + i * 7)
    keys.push(getCurrentWeekKey(d))
  }
  return [...new Set(keys)].sort()
}

export function getActiveDays(week: WeekSchedule): DayName[] {
  return sortDays(week.activeDays.length ? week.activeDays : DEFAULT_DAYS.slice())
}

export function weekHasAnyRoute(week: WeekSchedule): boolean {
  const days = week.days
  return getActiveDays(week).some((day) => {
    const route = days[day]
    return route?.title
  })
}

export function getAvailableDays(activeDays: DayName[]): DayName[] {
  return DAY_ORDER.filter((d) => !activeDays.includes(d))
}

export function isDefaultDay(day: DayName): boolean {
  return DEFAULT_DAYS.includes(day)
}

export function isValidWeekKey(weekKey: string): boolean {
  return /^\d{4}-W\d{2}$/.test(weekKey)
}

export function isDayName(value: string): value is DayName {
  return (DAY_ORDER as readonly string[]).includes(value)
}

function previewForUrl(url: string): string | undefined {
  try {
    return previewImageUrl(url)
  } catch {
    return undefined
  }
}

function enrichDayRoute(day: DayName, stored: StoredDayRoute): DayRoute {
  let thumbLabel: string | undefined
  try {
    const id = rwgpsIdFromUrl(stored.routeUrl)
    thumbLabel = `${DAY_LABELS[day]} · RWGPS ${id}`
  } catch {
    thumbLabel = `${DAY_LABELS[day]} route`
  }

  return {
    title: stored.routeName,
    url: stored.routeUrl,
    previewImageUrl: previewForUrl(stored.routeUrl),
    thumbLabel,
  }
}

export function enrichStoredWeek(weekKey: string, stored?: StoredWeek): WeekSchedule {
  const activeDays = sortDays(
    (stored?.activeDays ?? DEFAULT_DAYS).filter(isDayName),
  )
  const days: Partial<Record<DayName, DayRoute>> = {}

  for (const [dayKey, route] of Object.entries(stored?.days ?? {})) {
    if (!isDayName(dayKey)) continue
    days[dayKey] = enrichDayRoute(dayKey, route)
  }

  return {
    key: weekKey,
    label: getWeekLabel(weekKey),
    activeDays: activeDays.length ? activeDays : [...DEFAULT_DAYS],
    days,
  }
}

export function storedWeekFromInput(
  activeDays: DayName[],
  days: Partial<Record<DayName, StoredDayRoute>>,
): StoredWeek {
  const normalizedDays: Record<string, StoredDayRoute> = {}
  for (const day of activeDays) {
    const route = days[day]
    if (!route?.routeName.trim() || !route?.routeUrl.trim()) continue
    normalizedDays[day] = {
      routeName: route.routeName.trim(),
      routeUrl: route.routeUrl.trim(),
    }
  }

  return {
    activeDays: sortDays(activeDays),
    days: normalizedDays,
  }
}

export function migrateEventsToWeeksFile(events: LegacyEvent[]): WeeksFile {
  const byWeek = new Map<string, StoredWeek>()

  for (const event of events) {
    const day = dayFromDate(event.date)
    if (!day) continue

    const weekKey = getCurrentWeekKey(new Date(event.date + "T12:00:00Z"))
    let week = byWeek.get(weekKey)
    if (!week) {
      week = { activeDays: [...DEFAULT_DAYS], days: {} }
      byWeek.set(weekKey, week)
    }

    if (!week.activeDays.includes(day)) {
      week.activeDays = sortDays([...week.activeDays.filter(isDayName), day])
    }

    week.days[day] = {
      routeName: event.routeName,
      routeUrl: event.routeUrl,
    }
  }

  return Object.fromEntries(byWeek)
}

export function buildWeekScheduleMap(
  weeksFile: WeeksFile,
  centerKey = getCurrentWeekKey(),
  radius = 8,
): { weeks: Map<string, WeekSchedule>; weekKeys: string[] } {
  const weekKeys = getWeekKeysWindow(centerKey, radius)
  const weeks = new Map<string, WeekSchedule>()

  for (const key of weekKeys) {
    weeks.set(key, enrichStoredWeek(key, weeksFile[key]))
  }

  return { weeks, weekKeys }
}

export function getWeekFromMap(
  weeks: Map<string, WeekSchedule>,
  key: string,
): WeekSchedule {
  return weeks.get(key) ?? enrichStoredWeek(key)
}
